import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient} from 'react-query'
import { Node } from 'slate';
import clsx from 'clsx';
import { toast } from 'components/Shared/Toast';
import { Button, CardHeader, ListGroup, ListGroupItem, Media, Nav, NavItem, NavLink, Input } from 'reactstrap';

import { fetcher } from 'api/client';
import { fetchSuggestions, deleteSuggestion, updateSuggestion } from 'api/actions/suggestions';
import { truncate } from 'utils';

import UpgradePlan from 'components/Shared/UpgradePlan'
import { deserializeHTML } from '../../Canvas/TratoFormat';
import { useProfileAbility } from 'context/profile'
import { useElementList } from './ElementList'
import bootbox from 'components/Modals/Bootbox';
import { SearchableDiscreteColorLegend } from 'react-vis';
import useDebounce from 'hooks/useDebounce';

export function SuggestionList() {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const profileAbility = useProfileAbility();
    const { elementsMap } = useElementList();
    const [currentTab,setCurrentTab] = useState('own');
    const [search, setSearch] = useState('');
    const debounceSearch = useDebounce(search, 500);
    const canViewSetSuggestions = profileAbility.can('VIEW_SET_SUGGESTIONS'); // can VIEW_SET_SUGGESTIONS
    const canShareSuggestions = profileAbility.can('SHARE_SUGGESTIONS'); // can CAN_SHARE_SUGGESTIONS
    const { data } = useQuery(['sugggestions', {search: debounceSearch, isPublished: currentTab === 'public'}], () => fetcher(fetchSuggestions({search: debounceSearch, isPublished: currentTab === 'public'})), {
        enabled: canViewSetSuggestions
    })
    const { mutate: mutateRemoveSuggestion } = useMutation((values) => fetcher(deleteSuggestion(values)), {
        onSuccess: (data) => {
            queryClient.invalidateQueries('sugggestions')
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        }
    })
    const { mutate: mutateUpdateSuggestion } = useMutation((values) => fetcher(updateSuggestion(values)), {
        onSuccess: (data) => {
            queryClient.invalidateQueries('sugggestions')
        },
        onError: (error) => {
            toast.error(t(`errors.${error.message}`, t('errors.GeneralError')))
        }
    })

    const handleRemove = (suggestion) => {
        mutateRemoveSuggestion({ suggestionId: suggestion._id })
    }
    const handleAdd = ({ content }) => {
        const clause = elementsMap.get('clause');
        clause.onAdd({ text: content })
    }
    const handleShare = (suggestion) => {
        mutateUpdateSuggestion({ suggestionId: suggestion._id, isPublished: true }, {
            onSuccess: (data) => {
                toast.success("La sugerencia se compartió con éxito")
            }
        })
    }
     const handleUnshare = (suggestion) => {
        mutateUpdateSuggestion({ suggestionId: suggestion._id, isPublished: false }, {
            onSuccess: (data) => {
                toast.success("Haz dejado de compartir la sugerencia con éxito")
            }
        })
    }

    if (!canViewSetSuggestions) {
        return <UpgradePlan inverted />
    }

    return (
        <div>
             <Nav className="" tabs fill>
                <NavItem onClick={() => setCurrentTab('public')}>
                    <NavLink className={clsx('p-1', currentTab === 'public' ? 'active bg-light border-bottom-light' : 'bg-white text-dark')}>
                        <span>
                            Trato
                        </span>
                    </NavLink>
                </NavItem>
                <NavItem onClick={() => setCurrentTab('own')}>
                    <NavLink className={clsx('p-1', currentTab === 'own' ? 'active bg-light border-bottom-light' : 'bg-white text-dark')}>
                        <span>
                            Propias
                        </span>
                    </NavLink>
                </NavItem>
            </Nav>
            <div className="p-2">
                <Input 
                    className={clsx('form-control input-sm search-variable')}
                    placeholder={t('Buscar')}
                    value={search}
                    onChange={(evt) => setSearch(evt.target.value)}
                />
            </div>
            <ListGroup className="text-dark">
                {
                    data?.map(suggestion => (
                        <SuggestionItem 
                            suggestion={suggestion} 
                            onAdd={handleAdd} 
                            onRemove={handleRemove} 
                            onShare={handleShare} 
                            onUnshare={handleUnshare}
                            canShare={canShareSuggestions && currentTab === 'own'} canRemove={currentTab === 'own'} 
                        />
                    ))
                }
                { !data?.length &&
                    <ListGroupItem className="bg-light">{t('editor.suggestions.empty')}</ListGroupItem>
                }
            </ListGroup>
        </div>
    )
}

function SuggestionItem({ suggestion, canShare, canRemove, onAdd, onRemove, onShare, onUnshare }) {
    const { t }= useTranslation();
    // TODO: handle suggestions migration;
    const [content, children] = useMemo(() => {
        const children = suggestion.children?.length ? suggestion.children : deserializeHTML(suggestion.content, { contract: {}, participants: new Map() });

        return [Node.string({ type: 'text', children }), children]
    }, [suggestion.content, suggestion.children])

    const handleCopyToClipboard = (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        navigator.clipboard.writeText(content)
        toast.success(t('common.copied_clipboard'));
    }

    const handleRemove = async (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        const resp = await bootbox.confirm('¿Quieres eliminar esta sugerencia?');
        if (!resp) return;
        onRemove({
            ...suggestion,
            children,
            content
        })
    }
    const handleAdd = (evt) => {
        onAdd({
            content
        })
    }
    const handleShare = async (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        const resp = await bootbox.confirm('¿Quieres compartir esta sugerencia?');
        if (!resp) return;
        onShare({
            ...suggestion,
            children,
            content
        })
    }
    const handleUnshare = async (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        const resp = await bootbox.confirm('¿Quieres dejar de compartir esta sugerencia?');
        if (!resp) return;
        onUnshare({
            ...suggestion,
            children,
            content
        })
    }

    return (
        <ListGroupItem className="p-0 d-flex justify-content-between" onClick={handleAdd}>
            <Media className="p-2 d-flex flex-column">
                <div className="font-weight-bold">{suggestion.name}</div>
                <div>{truncate(content, 48)}</div>
            </Media>
            <div className="d-flex flex-column">
                <Button color="light" size="sm" title="Eliminar sugerencia" onClick={handleRemove} hidden={!canRemove}>
                    <i className="fas fa-times text-danger"></i>
                </Button>
                <Button color="light" size="sm" title="Compartir sugerencia" onClick={handleShare} hidden={!canShare || suggestion.isPublished}>
                    <i className="fas fa-share text-secondary"></i>
                </Button>
                <Button color="light" size="sm" title="Dejar de compartir sugerencia" onClick={handleUnshare} hidden={!canShare || !suggestion.isPublished}>
                    <i className="fas fa-ban text-secondary"></i>
                </Button>
                <Button  color="light"x title="Copiado" size="sm" onClick={handleCopyToClipboard}>
                    <i className="fas fa-clone text-dark"></i>
                </Button>
            </div>
        </ListGroupItem>
    )
}